import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import LandingPage from './pages/LandingPage';
import EmailAuth from './pages/EmailAuth';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ContactUs from './pages/ContactUs';
import Services from './pages/Services';
import JoinInvitation from './pages/JoinInvitation';
import OrganizationForm from './pages/OrganizationForm';
import CoreAssessment from './pages/CoreAssessment';
import TechnicalControls from './pages/TechnicalControls';
import DocumentationReadiness from './pages/DocumentationReadiness';
import AssessmentSummary from './pages/AssessmentSummary';
import AssessmentPage from './pages/AssessmentPage';
import ProAssessment from './pages/ProAssessment';
import Dashboard from './pages/Dashboard';
import ResultsPage from './pages/ResultsPage';
import AdminPanel from './pages/AdminPanel';
import AuditLogs from './pages/AuditLogs';
import KnowledgeBase from './pages/KnowledgeBase';
import Organizations from './pages/Organizations';
import FreeChecklist from './pages/FreeChecklist';
import ChoosePlan from './pages/ChoosePlan';
import PaymentSuccess from './pages/PaymentSuccess';
import ISMSDocumentControl from './pages/ISMSDocumentControl';
import Footer from './components/Footer';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import './App.css';

const ProtectedRoute = ({ children, showFooter = true }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1">
        {children}
      </div>
      {showFooter && <Footer />}
    </div>
  );
};

const AdminRoute = ({ children, showFooter = true }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Allow super_admin, admin, org_admin, and security_officer
  if (!user || !['admin', 'super_admin', 'org_admin', 'security_officer'].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1">
        {children}
      </div>
      {showFooter && <Footer />}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <BrowserRouter>
          <div className="App">
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/download-now" element={<FreeChecklist />} />
            <Route path="/auth" element={<EmailAuth />} />
            <Route path="/login" element={<EmailAuth />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/choose-plan" element={
              <ProtectedRoute showFooter={false}>
                <ChoosePlan />
              </ProtectedRoute>
            } />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/services" element={<Services />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/join/:token" element={<JoinInvitation />} />
            <Route path="/organization" element={
              <ProtectedRoute>
                <OrganizationForm />
              </ProtectedRoute>
            } />
            <Route path="/core-assessment/:orgId" element={
              <ProtectedRoute>
                <CoreAssessment />
              </ProtectedRoute>
            } />
            <Route path="/technical-controls/:orgId" element={
              <ProtectedRoute>
                <TechnicalControls />
              </ProtectedRoute>
            } />
            <Route path="/documentation-readiness/:orgId" element={
              <ProtectedRoute>
                <DocumentationReadiness />
              </ProtectedRoute>
            } />
            <Route path="/controls-assessment/:orgId" element={
              <ProtectedRoute>
                <ProAssessment />
              </ProtectedRoute>
            } />
            <Route path="/assessment-summary/:orgId" element={
              <ProtectedRoute>
                <AssessmentSummary />
              </ProtectedRoute>
            } />
            <Route path="/assessment/:assessmentId" element={
              <ProtectedRoute>
                <AssessmentPage />
              </ProtectedRoute>
            } />
            <Route path="/pro-assessment/:orgId" element={
              <ProtectedRoute>
                <ProAssessment />
              </ProtectedRoute>
            } />
            <Route path="/pro-assessment-summary/:orgId" element={
              <ProtectedRoute>
                <AssessmentSummary />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/results/:assessmentId" element={
              <ProtectedRoute>
                <ResultsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <AdminRoute>
                <AdminPanel />
              </AdminRoute>
            } />
            <Route path="/audit-logs" element={
              <ProtectedRoute>
                <AuditLogs />
              </ProtectedRoute>
            } />
            <Route path="/knowledge-base" element={
              <ProtectedRoute>
                <KnowledgeBase />
              </ProtectedRoute>
            } />
            <Route path="/isms-document-control" element={
              <ProtectedRoute showFooter={false}>
                <ISMSDocumentControl />
              </ProtectedRoute>
            } />
            <Route path="/organizations" element={
              <ProtectedRoute>
                <Organizations />
              </ProtectedRoute>
            } />
          </Routes>
          <Toaster 
            position="top-right" 
            richColors 
            closeButton={true}
            duration={5000}
          />
        </div>
      </BrowserRouter>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
